import {
  ChevronDown,
  ChevronUp,
  Layers3,
  LoaderCircle,
  Pause,
} from "lucide-react";
import { useEffect, useRef } from "react";
import type { GroupedToolCall } from "../group-tool-messages";
import type { ChatToolMessage } from "../types";
import { CompactToolRow } from "./CompactToolRow";
import { RunningToolCall } from "./RunningToolCall";
import { getToolGroupSummary } from "./tool-call-presentation";

export const ToolGroup = ({
  calls,
  results,
  isLoading,
  expanded,
  onToggle,
  reviewByCall,
  decisions,
  pausedBySubagent = false,
  connectionNames = [],
}: {
  calls: GroupedToolCall[];
  results: ReadonlyMap<string, ChatToolMessage>;
  isLoading: boolean;
  expanded: boolean;
  onToggle: () => void;
  reviewByCall: ReadonlyMap<string, string>;
  decisions: ReadonlyMap<string, "approve" | "reject" | "edit">;
  pausedBySubagent?: boolean;
  connectionNames?: string[];
}) => {
  const pending = reviewByCall.size > 0 || pausedBySubagent;
  const pendingRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (pending)
      pendingRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
      });
  }, [pending]);
  const summary = getToolGroupSummary(
    calls.map(({ toolCall }) => toolCall.name ?? ""),
    connectionNames
  );
  const title = pending
    ? `Used ${calls.length} ${calls.length === 1 ? "tool" : "tools"} · ${pausedBySubagent && reviewByCall.size === 0 ? "sub-agent paused for your approval" : "paused for your approval"}`
    : summary;
  // Calls follow stream order; result insertion order records completion order.
  const activeCall = [...calls]
    .reverse()
    .find((call) => !results.has(call.id) && !call.toolCall.error);
  const completedCall = [...results.keys()]
    .reverse()
    .map((id) => calls.find((call) => call.id === id))
    .find(Boolean);
  const currentCall = activeCall ?? completedCall ?? calls.at(-1);
  const Chevron = expanded ? ChevronUp : ChevronDown;
  const Icon = pending ? Pause : isLoading ? LoaderCircle : Layers3;

  return (
    <div ref={pendingRef} className="min-w-0" data-tool-group="true">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className={`flex min-h-[30px] w-full min-w-0 cursor-pointer items-center gap-2 rounded-[3.5px] border-0 bg-transparent px-1 text-left text-[13px] leading-5 hover:bg-black/[.04] ${pending ? "text-[#b45309]" : "text-[#6b7280]"}`}
      >
        <Icon
          aria-hidden="true"
          className={`h-[16px] w-[16px] shrink-0 ${isLoading && !pending ? "animate-spin text-[#074ee8]" : ""}`}
        />
        {isLoading && !pending && currentCall ? (
          <>
            <span className="shrink-0 text-black/[.87]">Working</span>
            <span aria-hidden="true" className="shrink-0">
              ·
            </span>
            <RunningToolCall
              call={currentCall}
              result={results.get(currentCall.id)}
            />
            <span className="shrink-0">
              · {calls.length} {calls.length === 1 ? "tool" : "tools"}
            </span>
          </>
        ) : (
          <span className="min-w-0 flex-1 truncate" title={title}>
            {title}
          </span>
        )}
        <Chevron aria-hidden="true" className="h-[14px] w-[14px] shrink-0" />
      </button>
      <div hidden={!expanded} className="min-w-0 pl-[7px]">
        {calls.map((call) => (
          <div key={call.id}>
            <CompactToolRow
              call={call}
              result={results.get(call.id)}
              running={isLoading}
              reviewKey={reviewByCall.get(call.id)}
              decision={decisions.get(call.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
